import { formatWeekday, t } from "@hakili/i18n";
import {
  ActionBar,
  BANK_BORDER,
  BANK_BORDER_SOFT,
  BANK_FS_BODY,
  BANK_FS_META,
  BANK_FS_READ,
  BANK_TEXT,
  BANK_TEXT_2,
  Button,
  GuardedButton,
  ProgressRing,
} from "@hakili/ui";
import { ArrowRight, Check, Clock } from "lucide-react";
import { type ReactElement, useState } from "react";

import { D2Card, D2Chip, GUARD_CHIP, GUARD_FOOT } from "./d2-parts.js";
import { dayLabel } from "./comite.js";
import { DASH, fill } from "./dossiers.js";
import type { FundsMoment } from "./funds-moment.js";
import { ObligationsCard } from "./obligations-card.js";
import { OrdrePanel, type OrdreRead } from "./ordre.js";

/**
 * Round `T-1b`, `M1` (`P2-BKW-005`; WF-BNK-16): **the conditions préalables to the drawing**
 * (`hakili-bank.jsx`, `ConditionsPanel`, `conditionsSuspensives`, `conditionsUlterieures`)
 * , the header with the contract's date and the count from the rows, the chip
 * « Décaissement bloqué » / « Prêt à décaisser »; the five rows of
 * `lending.bank_file_conditions` each with its label, its party and its piece from the
 * definition, its wait derived from the row's own date, « Pièce reçue » by the analyst's
 * door, « Relancer » greyed (no chase door, R81); « Échéances à surveiller » from what the
 * platform holds (the report's ninety days, the proforma a dash with its reason, the
 * insurance row absent while parked); « Obligations postérieures au décaissement » as
 * text; the right column by WF-BNK-16 step 5, which WINS over the drawing's single
 * beneficiary , the instruction with the two fees on two lines (I2), the order as TWO
 * transfers by `payments`' one quote, each beneficiary's account as the platform holds it
 * or a dash with its reason; « Ordonner les deux virements » greyed with `T-2`.
 */
export interface BankConditionRead {
  readonly key: string;
  readonly label: string;
  readonly kind: "suspensive" | "ulterieure";
  readonly providedBy: string;
  /** Who lifts the row, as the door types it: only `banque` draws « Pièce reçue » (a control with no door is not drawn). */
  readonly liftedBy: "banque" | "hakili" | null;
  readonly evidenceLabel: string;
  readonly state: "attente" | "verification" | "levee";
  readonly createdAt: string;
  readonly waitingDays: number;
  readonly liftedAt: string | null;
  /**
   * `COMITE-RECORD-1` (D2 frame 119): where the row comes from, typed. `comite`: a condition the committee fixed (one per line of the decision the chef
   * recorded), listed after the bank's own; `committee` names the sitting and who recorded it. A definition row carries neither.
   */
  readonly origin: "definition" | "comite";
  readonly committee: { readonly sittingDate: string; readonly recordedBy: string } | null;
}

export interface ConditionsRead {
  readonly conditions: readonly BankConditionRead[];
  readonly liftedCount: number;
  readonly allLifted: boolean;
}

export interface DisbursementRead {
  readonly contractSignedAt: string | null;
  readonly deposit: { readonly amount: number | null; readonly state: "attendu" | "depose" };
  readonly loanVehicleAmount: number;
  readonly salePriceAmount: number | null;
  readonly fees: {
    readonly bankAmount: number;
    readonly hakiliAmount: number;
    readonly financesBankFee: boolean;
    readonly financesHakiliFee: boolean;
  };
  readonly quote: {
    readonly ratePct: number;
    /** `ODF-2`: the price's two shares, said by the server (null when the read does not carry them). */
    readonly dealerSharePct: number | null;
    readonly hakiliSharePct: number | null;
    readonly hakiliAmount: number;
    readonly dealerAmount: number;
    /** `T-1c` (I31 amended): where the rate came from , the dealer's negotiated convention term, or the standard. */
    readonly source: "convention" | "standard";
  } | null;
  readonly hakiliAccount: {
    readonly bankName: string;
    readonly accountName: string;
    readonly accountNumber: string;
  } | null;
  readonly reportValidity: { readonly reportedAt: string; readonly validUntil: string } | null;
}

export interface LaterObligation {
  readonly key: string;
  readonly label: string;
  readonly dueDays: number | null;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);
const rec = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
const stateOf = (value: unknown): BankConditionRead["state"] | null =>
  value === "attente" || value === "verification" || value === "levee" ? value : null;
const kindOf = (value: unknown): BankConditionRead["kind"] | null =>
  value === "suspensive" || value === "ulterieure" ? value : null;

const committeeOf = (value: unknown): BankConditionRead["committee"] => {
  const sitting = rec(value);
  const sittingDate = str(sitting?.["sittingDate"]);
  const recordedBy = str(sitting?.["recordedBy"]);
  return sittingDate === null || recordedBy === null ? null : { sittingDate, recordedBy };
};

const liftedByOf = (value: unknown): BankConditionRead["liftedBy"] =>
  value === "banque" || value === "hakili" ? value : null;

export function conditionsFrom(answer: unknown): ConditionsRead | null {
  const raw = rec(rec(answer)?.["conditions"]);
  if (raw === null || !Array.isArray(raw["conditions"])) return null;
  const conditions = raw["conditions"].flatMap((one: unknown) => {
    const row = rec(one);
    const state = stateOf(row?.["state"]);
    const kind = kindOf(row?.["kind"]);
    return row !== null && typeof row["key"] === "string" && state !== null && kind !== null
      ? [
          {
            key: row["key"],
            label: str(row["label"]) ?? "",
            kind,
            providedBy: str(row["providedBy"]) ?? "",
            liftedBy: liftedByOf(row["liftedBy"]),
            evidenceLabel: str(row["evidenceLabel"]) ?? "",
            state,
            createdAt: str(row["createdAt"]) ?? "",
            waitingDays: num(row["waitingDays"]) ?? 0,
            liftedAt: str(row["liftedAt"]),
            origin: row["origin"] === "comite" ? ("comite" as const) : ("definition" as const),
            committee: committeeOf(row["committee"]),
          },
        ]
      : [];
  });
  return {
    conditions,
    liftedCount: num(raw["liftedCount"]) ?? conditions.filter((one) => one.state === "levee").length,
    allLifted: raw["allLifted"] === true,
  };
}

export function disbursementFrom(answer: unknown): DisbursementRead | null {
  const raw = rec(rec(answer)?.["disbursement"]);
  if (raw === null) return null;
  const deposit = rec(raw["deposit"]);
  const fees = rec(raw["fees"]);
  const quote = rec(raw["quote"]);
  const account = rec(raw["hakiliAccount"]);
  const validity = rec(raw["reportValidity"]);
  return {
    contractSignedAt: str(raw["contractSignedAt"]),
    deposit: {
      amount: num(deposit?.["amount"]),
      state: deposit?.["state"] === "depose" ? "depose" : "attendu",
    },
    loanVehicleAmount: num(raw["loanVehicleAmount"]) ?? 0,
    salePriceAmount: num(raw["salePriceAmount"]),
    fees: {
      bankAmount: num(fees?.["bankAmount"]) ?? 0,
      hakiliAmount: num(fees?.["hakiliAmount"]) ?? 0,
      financesBankFee: fees?.["financesBankFee"] === true,
      financesHakiliFee: fees?.["financesHakiliFee"] === true,
    },
    quote:
      quote === null
        ? null
        : {
            ratePct: num(quote["ratePct"]) ?? 0,
            dealerSharePct: num(quote["dealerSharePct"]),
            hakiliSharePct: num(quote["hakiliSharePct"]),
            hakiliAmount: num(quote["hakiliAmount"]) ?? 0,
            dealerAmount: num(quote["dealerAmount"]) ?? 0,
            source: quote["source"] === "convention" ? "convention" : "standard",
          },
    hakiliAccount:
      account !== null &&
      typeof account["bankName"] === "string" &&
      typeof account["accountName"] === "string" &&
      typeof account["accountNumber"] === "string"
        ? {
            bankName: account["bankName"],
            accountName: account["accountName"],
            accountNumber: account["accountNumber"],
          }
        : null,
    reportValidity:
      validity !== null &&
      typeof validity["reportedAt"] === "string" &&
      typeof validity["validUntil"] === "string"
        ? { reportedAt: validity["reportedAt"], validUntil: validity["validUntil"] }
        : null,
  };
}

export function laterObligationsFrom(answer: unknown): readonly LaterObligation[] {
  const raw = rec(answer)?.["laterObligations"];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((one: unknown) => {
    const row = rec(one);
    return row !== null && typeof row["key"] === "string"
      ? [{ key: row["key"], label: str(row["label"]) ?? "", dueDays: num(row["dueDays"]) }]
      : [];
  });
}

/** The party's words, keyed by the definition's code (a wire value). */
const PARTIES = new Map<string, string>([
  ["employeur", "bank.conditions.partieEmployeur"],
  ["assureur", "bank.conditions.partieAssureur"],
  ["client", "bank.conditions.partieClient"],
  ["notaire", "bank.conditions.partieNotaire"],
  ["hakili", "bank.conditions.partieHakili"],
]);

/**
 * DESIGN SPRINT D-1026-2 (Lane E), D2 frame 46: the words of the six conditions, by the definition's typed code. The definitions are
 * the server's seeded rows (`lending.bank_condition_definitions`, read elsewhere too): they are not edited, the bank maps the code to
 * the drawing's title and « who · what proves it » line, and a code with no mapping keeps the door's words.
 */
const DRAWN = new Map<string, { readonly title: string; readonly line: string }>([
  [
    "domiciliation",
    { title: "bank.conditions.d2Domiciliation", line: "bank.conditions.d2DomiciliationLigne" },
  ],
  [
    "assuranceEmprunteur",
    { title: "bank.conditions.d2AssuranceEmprunteur", line: "bank.conditions.d2AssuranceEmprunteurLigne" },
  ],
  [
    "assuranceAuto",
    { title: "bank.conditions.d2AssuranceAuto", line: "bank.conditions.d2AssuranceAutoLigne" },
  ],
  ["gage", { title: "bank.conditions.d2Gage", line: "bank.conditions.d2GageLigne" }],
  ["apport", { title: "bank.conditions.d2Apport", line: "bank.conditions.d2ApportLigne" }],
  ["fraisHakili", { title: "bank.conditions.d2FraisHakili", line: "bank.conditions.d2FraisHakiliLigne" }],
]);

/** « en attente depuis N jours »; a wait under one day is the drawing's bare « en attente » (D2 draws no « 0 jours »). */
const waitWords = (days: number): string =>
  days === 0
    ? t("bank.conditions.attenteSimple")
    : days === 1
      ? t("bank.conditions.attenteUnJour")
      : fill("bank.conditions.attenteJours", { n: String(days) });

/** D2: « jeudi 22 octobre », no year. */
const weekdayDayMonth = (iso: string): string => formatWeekday(new Date(iso)).replace(/ \d{4}$/u, "");

function ConditionsView(props: {
  readonly conditions: ConditionsRead;
  readonly disbursement: DisbursementRead | null;
  readonly laterObligations: readonly LaterObligation[];
  readonly busy: boolean;
  readonly onLift: (key: string) => void;
  readonly onNext: () => void;
}): ReactElement {
  const { conditions, disbursement } = props;
  const suspensive = conditions.conditions.filter((one) => one.kind === "suspensive");
  const total = suspensive.length;
  const remaining = suspensive.filter((one) => one.state !== "levee").length;
  const all = total > 0 && remaining === 0;
  const signedOn =
    disbursement?.contractSignedAt === null || disbursement === null
      ? DASH
      : weekdayDayMonth(disbursement.contractSignedAt);
  return (
    <div>
      <D2Card
        title={t("bank.conditions.suspensives")}
        flush
        testId="bank-conditions-suspensives"
        sub={t("bank.conditions.toutesRequises")}
        right={
          <D2Chip tone={all ? "ok" : "warn"} testId="bank-conditions-chip">
            {all ? t("bank.conditions.pretADecaisser") : t("bank.conditions.decaissementBloque")}
          </D2Chip>
        }
      >
        <ul>
          {suspensive.map((condition) => {
            const drawn = DRAWN.get(condition.key);
            const lifted = condition.state === "levee";
            // D2 frame 119 (`analyst.js:234`): the committee's row draws no wait, only « en attente ».
            const committeeRow = condition.origin === "comite";
            return (
              <li
                key={condition.key}
                className={`grid grid-cols-[44px_minmax(0,1fr)_170px_auto] items-center gap-3.5 border-b px-5 py-3.5 last:border-b-0 ${BANK_BORDER_SOFT}`}
                data-testid={`bank-condition-${condition.key}`}
                data-state={condition.state}
              >
                <span
                  className={`grid h-11 w-11 place-items-center rounded-full border-2 ${
                    lifted
                      ? "border-[color:var(--hk-ok)] bg-[color:var(--hk-ok)] text-white"
                      : `${BANK_BORDER} bg-white ${BANK_TEXT_2}`
                  }`}
                >
                  {lifted ? (
                    <Check size={20} strokeWidth={2} aria-hidden="true" />
                  ) : (
                    <Clock size={20} strokeWidth={2} aria-hidden="true" />
                  )}
                </span>
                <div className={`min-w-0 ${BANK_FS_READ}`}>
                  <b className={`block font-semibold ${BANK_TEXT}`}>
                    {committeeRow
                      ? fill("bank.conditions.conditionComite", { texte: condition.label })
                      : drawn === undefined
                        ? condition.label
                        : t(drawn.title)}
                  </b>
                  <small className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}>
                    {condition.origin === "comite" && condition.committee !== null
                      ? fill("bank.conditions.fixeeParComite", {
                          date: dayLabel(condition.committee.sittingDate),
                          chef: condition.committee.recordedBy,
                        })
                      : drawn === undefined
                        ? `${t(PARTIES.get(condition.providedBy) ?? "bank.conditions.partieClient")} · ${condition.evidenceLabel}`
                        : t(drawn.line)}
                  </small>
                </div>
                <span
                  className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}
                  data-testid={`bank-condition-attente-${condition.key}`}
                >
                  {lifted
                    ? t("bank.conditions.pieceRecue")
                    : committeeRow
                      ? t("bank.conditions.attenteSimple")
                      : waitWords(condition.waitingDays)}
                </span>
                <span className="flex justify-end">
                  {lifted ? (
                    <D2Chip tone="ok" testId={`bank-condition-etat-${condition.key}`}>
                      <Check size={14} strokeWidth={2.6} aria-hidden="true" />
                      {t("bank.conditions.etatLevee")}
                    </D2Chip>
                  ) : condition.liftedBy === "hakili" ? (
                    <D2Chip testId={`bank-condition-hakili-${condition.key}`}>
                      {t("bank.conditions.leveeParHakili")}
                    </D2Chip>
                  ) : condition.liftedBy === "banque" ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="whitespace-nowrap"
                      disabled={props.busy}
                      aria-label={`${t("bank.conditions.pieceRecue")} : ${drawn === undefined || committeeRow ? condition.label : t(drawn.title)}`}
                      onClick={() => {
                        props.onLift(condition.key);
                      }}
                      data-testid={`bank-condition-recevoir-${condition.key}`}
                    >
                      {t("bank.conditions.pieceRecue")}
                    </Button>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      </D2Card>

      <div className="grid grid-cols-1 items-start gap-x-4 min-[1100px]:grid-cols-2">
        <D2Card title={t("bank.conditions.avancement")} testId="bank-conditions-avancement">
          <div className="flex items-center gap-[18px]">
            <ProgressRing
              label={fill("bank.conditions.progression", {
                n: String(conditions.liftedCount),
                total: String(total),
              })}
              value={total === 0 ? 0 : (100 * conditions.liftedCount) / total}
              size={120}
            >
              <span className="text-center leading-[1.1]">
                <b
                  className={`font-semibold tabular-nums ${BANK_TEXT} text-[26px] [font-family:var(--hk-mono)]`}
                >
                  {conditions.liftedCount}/{total}
                </b>
                <small className={`block ${BANK_FS_META} ${BANK_TEXT_2}`}>
                  {t("bank.conditions.leveesMot")}
                </small>
              </span>
            </ProgressRing>
            <p className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}>
              {fill("bank.conditions.avancementTexte", { date: signedOn })}
            </p>
          </div>
        </D2Card>
        <ObligationsCard variant="apres" obligations={props.laterObligations} />
      </div>

      <ActionBar label={t("bank.conditions.barreActions")}>
        {all ? (
          <Button
            className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap px-[22px] text-[length:var(--hk-fs-read)]"
            disabled={props.busy}
            onClick={props.onNext}
            data-testid="bank-conditions-passer-ordre"
          >
            {t("bank.conditions.passerOrdre")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : (
          <GuardedButton
            disabled
            {...GUARD_CHIP}
            reason={fill(remaining === 1 ? "bank.conditions.restanteUne" : "bank.conditions.restantes", {
              n: String(remaining),
            })}
            className={GUARD_FOOT}
            testId="bank-conditions-passer-ordre"
          >
            <span className="inline-flex items-center gap-2">
              {t("bank.conditions.passerOrdre")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </GuardedButton>
        )}
      </ActionBar>
    </div>
  );
}

function OrdreView(props: {
  readonly disbursement: DisbursementRead | null;
  readonly busy: boolean;
  /** `T-2`: the ordre's read (`GET /bank/files/{id}/disbursement`) and its two acts. */
  readonly ordre: OrdreRead | null;
  readonly onOrder: () => void;
  readonly onFunds: (dealerTransferReference: string, hakiliTransferReference: string) => void;
  /** `ODF-1`: the funds act's moment and the page's reload, handed to the ordre. */
  readonly fundsMoment?: FundsMoment;
  readonly onReload?: () => void;
  /** Back to the conditions (D2 frame 47's « Revenir aux conditions »). */
  readonly onBack: () => void;
}): ReactElement {
  return (
    <OrdrePanel
      ordre={props.ordre}
      fees={
        props.disbursement === null
          ? null
          : {
              bankAmount: props.disbursement.fees.bankAmount,
              hakiliAmount: props.disbursement.fees.hakiliAmount,
            }
      }
      busy={props.busy}
      onOrder={props.onOrder}
      onFunds={props.onFunds}
      {...(props.fundsMoment === undefined ? {} : { fundsMoment: props.fundsMoment })}
      {...(props.onReload === undefined ? {} : { onReload: props.onReload })}
      onBack={props.onBack}
    />
  );
}

export function ConditionsPanel(props: {
  readonly conditions: ConditionsRead;
  readonly disbursement: DisbursementRead | null;
  readonly laterObligations: readonly LaterObligation[];
  readonly busy: boolean;
  readonly onLift: (key: string) => void;
  /** `T-2`: the ordre's read (`GET /bank/files/{id}/disbursement`) and its two acts. */
  readonly ordre: OrdreRead | null;
  readonly onOrder: () => void;
  readonly onFunds: (dealerTransferReference: string, hakiliTransferReference: string) => void;
  /** `ODF-1`: the funds act's moment and the page's reload, handed to the ordre's foot. */
  readonly fundsMoment?: FundsMoment;
  readonly onReload?: () => void;
  /** The view the page opens on; by default the ordre once its order is sent (or its funds are awaited), the conditions before. */
  readonly view?: "conditions" | "ordre";
}): ReactElement {
  const ordered =
    (props.ordre?.settlement?.disbursement ?? null) !== null ||
    (props.fundsMoment !== undefined && props.fundsMoment !== "idle");
  const [view, setView] = useState<"conditions" | "ordre">(props.view ?? (ordered ? "ordre" : "conditions"));
  return (
    <div data-testid="bank-conditions">
      {view === "ordre" ? (
        <OrdreView
          {...props}
          onBack={() => {
            setView("conditions");
          }}
        />
      ) : (
        <ConditionsView
          conditions={props.conditions}
          disbursement={props.disbursement}
          laterObligations={props.laterObligations}
          busy={props.busy}
          onLift={props.onLift}
          onNext={() => {
            setView("ordre");
          }}
        />
      )}
    </div>
  );
}
